import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'

import { useRealtimeEvent } from '@/lib/realtime'

import { notificationsApi } from './api'
import type { Notification, NotificationListResponse, NotificationPreferencesResponse } from './types'

export const notificationKeys = {
  all: (slug: string) => ['v2', 'notifications', slug] as const,
  feed: (slug: string, unreadOnly: boolean) => ['v2', 'notifications', slug, 'feed', unreadOnly] as const,
  unread: (slug: string) => ['v2', 'notifications', slug, 'unread'] as const,
  preferences: (slug: string) => ['v2', 'notifications', slug, 'preferences'] as const,
}

/**
 * The unread badge. Polled slowly as a safety net; the realtime stream is what
 * makes it move the moment something happens.
 */
export function useUnreadNotificationCount(slug: string | null) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: notificationKeys.unread(slug ?? ''),
    queryFn: () => notificationsApi.unreadCount(slug as string),
    enabled: Boolean(slug),
    refetchInterval: 60_000,
    staleTime: 15_000,
    select: (response) => response.unread,
  })

  useRealtimeEvent<Notification>('notification', (incoming) => {
    if (!slug) return
    queryClient.setQueryData<{ unread: number }>(notificationKeys.unread(slug), (current) => ({
      unread: (current?.unread ?? 0) + 1,
    }))
    // Prepend to any feed already loaded so the panel is current when opened.
    for (const unreadOnly of [true, false]) {
      queryClient.setQueryData<InfiniteData<NotificationListResponse>>(
        notificationKeys.feed(slug, unreadOnly),
        (current) => {
          if (!current || current.pages.length === 0) return current
          const [first, ...rest] = current.pages
          if (!first || first.data.some((n) => n.id === incoming.id)) return current
          return {
            ...current,
            pages: [{ ...first, data: [incoming, ...first.data], unread_count: first.unread_count + 1 }, ...rest],
          }
        },
      )
    }
  })

  return query
}

/** The feed, newest first, paged backwards by id. */
export function useNotificationFeed(slug: string | null, unreadOnly: boolean, enabled = true) {
  return useInfiniteQuery({
    queryKey: notificationKeys.feed(slug ?? '', unreadOnly),
    queryFn: ({ pageParam }) =>
      notificationsApi.list(slug as string, { unread: unreadOnly, before: pageParam ?? undefined, limit: 30 }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_before,
    enabled: Boolean(slug) && enabled,
    staleTime: 15_000,
  })
}

function setUnread(queryClient: ReturnType<typeof useQueryClient>, slug: string, unread: number) {
  queryClient.setQueryData(notificationKeys.unread(slug), { unread })
}

function patchFeeds(
  queryClient: ReturnType<typeof useQueryClient>,
  slug: string,
  update: (n: Notification) => Notification,
) {
  for (const unreadOnly of [true, false]) {
    queryClient.setQueryData<InfiniteData<NotificationListResponse>>(
      notificationKeys.feed(slug, unreadOnly),
      (current) =>
        current
          ? { ...current, pages: current.pages.map((page) => ({ ...page, data: page.data.map(update) })) }
          : current,
    )
  }
}

export function useNotificationMutations(slug: string | null) {
  const queryClient = useQueryClient()
  const key = slug ?? ''

  const markRead = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(key, id),
    onMutate: (id) => {
      const now = new Date().toISOString()
      patchFeeds(queryClient, key, (n) => (n.id === id && !n.read_at ? { ...n, read_at: now } : n))
    },
    onSuccess: ({ unread }) => setUnread(queryClient, key, unread),
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(key) }),
  })

  const markUnread = useMutation({
    mutationFn: (id: number) => notificationsApi.markUnread(key, id),
    onMutate: (id) => patchFeeds(queryClient, key, (n) => (n.id === id ? { ...n, read_at: null } : n)),
    onSuccess: ({ unread }) => setUnread(queryClient, key, unread),
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(key) }),
  })

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(key),
    onMutate: () => {
      const now = new Date().toISOString()
      patchFeeds(queryClient, key, (n) => (n.read_at ? n : { ...n, read_at: now }))
      setUnread(queryClient, key, 0)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(key) }),
  })

  return { markRead, markUnread, markAllRead }
}

export function useNotificationPreferences(slug: string | null) {
  return useQuery({
    queryKey: notificationKeys.preferences(slug ?? ''),
    queryFn: () => notificationsApi.preferences(slug as string),
    enabled: Boolean(slug),
    staleTime: 60_000,
  })
}

export function useUpdateNotificationPreferences(slug: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof notificationsApi.updatePreferences>[1]) =>
      notificationsApi.updatePreferences(slug as string, input),
    onSuccess: (response: NotificationPreferencesResponse) => {
      queryClient.setQueryData(notificationKeys.preferences(slug ?? ''), response)
    },
  })
}
